import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Building2, UploadCloud, Save, X } from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDelete, supabase } from '../../auth';

interface Company {
    id: string;
    name: string;
    domain: string;
    address: string;
    mobile: string;
    tel_fax: string;
    email: string;
    logo_url: string;
    watermark_opacity: number;
}

const CompanyManager: React.FC = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingCompany, setEditingCompany] = useState<Company | null>(null);
    const [formData, setFormData] = useState<Partial<Company>>({
        watermark_opacity: 0.2
    });
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
    const [confirmEdit, setConfirmEdit] = useState<boolean>(false);

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const res = await apiGet<{success: boolean, data: Company[]}>('/api/admin/companies');
            if (res.success) {
                setCompanies(res.data);
            }
        } catch (err: any) {
            console.error('Failed to fetch companies', err);
            alert('Error fetching companies');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            if (editingCompany) {
                await apiPut(`/api/admin/companies/${editingCompany.id}`, formData);
            } else {
                await apiPost('/api/admin/companies', formData);
            }
            setShowForm(false);
            setEditingCompany(null);
            setConfirmEdit(false);
            setFormData({ watermark_opacity: 0.2 });
            fetchCompanies();
        } catch (err: any) {
            console.error('Save failed', err);
            alert(`Error saving company: ${err.message}`);
        }
    };
    
    const preSave = (e: React.FormEvent) => {
        e.preventDefault();
        if (editingCompany) {
            setConfirmEdit(true);
        } else {
            handleSave(); // create immediately
        }
    }

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await apiDelete(`/api/admin/companies/${confirmDelete}`);
            setConfirmDelete(null);
            fetchCompanies();
        } catch (err: any) {
            alert(`Error deleting company: ${err.message}`);
        }
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setUploadingLogo(true);
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('company_logo')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data } = supabase.storage
                .from('company_logo')
                .getPublicUrl(filePath);

            setFormData(prev => ({ ...prev, logo_url: data.publicUrl }));
        } catch (err: any) {
            console.error('Upload error', err);
            alert(`Error uploading logo: ${err.message}`);
        } finally {
            setUploadingLogo(false);
        }
    };

    const openEditForm = (company: Company) => {
        setEditingCompany(company);
        setFormData(company);
        setShowForm(true);
    };

    const openCreateForm = () => {
        setEditingCompany(null);
        setFormData({ watermark_opacity: 0.2 });
        setShowForm(true);
    };

    if (loading && companies.length === 0) return <div className="p-8 text-center text-slate-500">Loading companies...</div>;

    return (
        <div className="space-y-6">
            {!showForm ? (
                <>
                    <div className="flex justify-between items-center">
                        <h2 className="text-lg font-medium text-slate-900 dark:text-white flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-indigo-500"/>
                            Registered Companies
                        </h2>
                        <button 
                            onClick={openCreateForm}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm font-medium shadow-sm"
                        >
                            <Plus className="w-4 h-4"/>
                            Add Company
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {companies.map(company => (
                            <div key={company.id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 flex flex-col">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-4">
                                        {company.logo_url ? (
                                            <div className="w-12 h-12 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-700 flex items-center justify-center overflow-hidden shrink-0 p-1">
                                                <img src={company.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                                            </div>
                                        ) : (
                                            <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center shrink-0">
                                                <Building2 className="w-6 h-6 text-slate-400" />
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="font-semibold text-slate-900 dark:text-white line-clamp-1" title={company.name}>{company.name}</h3>
                                            <span className="text-xs text-indigo-600 dark:text-indigo-400 font-mono bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded-full mt-1 inline-block">
                                                {company.domain || 'No Domain'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => openEditForm(company)} className="text-slate-400 hover:text-indigo-600 transition-colors">
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setConfirmDelete(company.id)} className="text-slate-400 hover:text-red-600 transition-colors">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="mt-auto space-y-2 text-sm text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-700 pt-4">
                                    {company.email && <div className="truncate">📧 {company.email}</div>}
                                    {company.tel_fax && <div className="truncate">☎️ {company.tel_fax}</div>}
                                    {company.mobile && <div className="truncate">📱 {company.mobile}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-medium text-slate-900 dark:text-white">
                            {editingCompany ? 'Edit Company' : 'Add New Company'}
                        </h2>
                        <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <form onSubmit={preSave} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name *</label>
                                <input required type="text" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email Domain (for Auto-Assign)</label>
                                <input type="text" placeholder="@thormarinesa.com" value={formData.domain || ''} onChange={e => setFormData({...formData, domain: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500 font-mono text-sm" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Address</label>
                                <textarea value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} rows={2} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500"></textarea>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Mobile</label>
                                <input type="text" value={formData.mobile || ''} onChange={e => setFormData({...formData, mobile: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Telephone / Fax</label>
                                <input type="text" value={formData.tel_fax || ''} onChange={e => setFormData({...formData, tel_fax: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">General Email</label>
                                <input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Watermark Opacity (0.0 - 1.0)</label>
                                <input type="number" step="0.05" min="0" max="1" value={formData.watermark_opacity ?? 0.2} onChange={e => setFormData({...formData, watermark_opacity: parseFloat(e.target.value)})} className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Company Logo</label>
                                <div className="flex items-center gap-6">
                                    {formData.logo_url && (
                                        <div className="w-24 h-24 border rounded-lg p-2 bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 flex items-center justify-center shrink-0">
                                            <img src={formData.logo_url} alt="Logo Preview" className="max-w-full max-h-full object-contain" />
                                        </div>
                                    )}
                                    <div className="flex-1">
                                        <label className="flex items-center justify-center w-full max-w-sm px-4 py-3 bg-white dark:bg-slate-800 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                                            <div className="flex flex-col items-center">
                                                <UploadCloud className="w-6 h-6 text-slate-400 mb-2" />
                                                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                                                    {uploadingLogo ? 'Uploading...' : 'Click to Upload Logo'}
                                                </span>
                                            </div>
                                            <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} />
                                        </label>
                                        <p className="mt-2 text-xs text-slate-500">Supported formats: PNG, JPG, JPEG, SVG</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-slate-200 dark:border-slate-700 mt-6">
                            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                                Cancel
                            </button>
                            <button type="submit" disabled={uploadingLogo} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors shadow-sm disabled:opacity-50">
                                <Save className="w-4 h-4" />
                                {editingCompany ? 'Update Company' : 'Save Company'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
            
            {/* Delete Confirmation Modal */}
            {confirmDelete && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setConfirmDelete(null)}></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
                            <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                        <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-slate-900 dark:text-white" id="modal-title">
                                            Delete Company
                                        </h3>
                                        <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                                            Şirketi silmek istediğinize emin misiniz? Bu işlem geri alınamaz. Bu şirkete bağlı kullanıcılar şirket bilgisini kaybedebilir.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button type="button" onClick={handleDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm">
                                    Evet, Sil
                                </button>
                                <button type="button" onClick={() => setConfirmDelete(null)} className="mt-3 w-full inline-flex justify-center rounded-md border border-slate-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm dark:bg-slate-700 dark:text-white dark:border-slate-600 dark:hover:bg-slate-600">
                                    İptal
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Confirmation Modal */}
            {confirmEdit && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setConfirmEdit(false)}></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full">
                            <div className="bg-white dark:bg-slate-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                        <Edit2 className="h-6 w-6 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                        <h3 className="text-lg leading-6 font-medium text-slate-900 dark:text-white" id="modal-title">
                                            Confirm Changes
                                        </h3>
                                        <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                                            Bu şirketin detaylarını değiştirmek istediğinize emin misiniz? Değişiklikler anında canlıya geçecektir.
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button type="button" onClick={handleSave} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm">
                                    Evet, Güncelle
                                </button>
                                <button type="button" onClick={() => setConfirmEdit(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-slate-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm dark:bg-slate-700 dark:text-white dark:border-slate-600 dark:hover:bg-slate-600">
                                    Geri Dön
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyManager;
